import { get, post, put, patch, del } from '@/services/api'
import type {
  MessagingPhoneNumber,
  MessagingPhoneNumberOption,
  MessageTemplate,
  SmsOptOut,
  MessagingSettings,
  MessageThread,
  Message,
  SmsSegmentsResult,
  BulkCsvPreview,
  BulkSendResult
} from '../types'

type Paginated<T> = {
  data: T[]
  pagination?: {
    current_page: number
    per_page: number
    total_count: number
    total_pages?: number
    next_page?: number
  }
}

export const ucUserMessagingService = {
  /**
   * Display a listing of messaging phone numbers.
   * GET /api/v1/messaging
   */
  async listPhoneNumbers(params?: Record<string, unknown>) {
    const response = await get<{ data: MessagingPhoneNumber[] }>('/messaging', { params })
    return response.data
  },

  /**
   * Display messaging phone number dashboard info.
   * GET /api/v1/messaging/phone-numbers/{id}
   */
  async getPhoneNumber(id: string): Promise<MessagingPhoneNumber> {
    const response = await get<{ data: MessagingPhoneNumber[] }>(`/messaging/phone-numbers/${id}`)
    const first = response.data.data?.[0]
    if (!first) {
      throw new Error('Phone number not found')
    }
    return first
  },

  /**
   * Dropdown options for messaging phone number switcher.
   * GET /api/v1/messaging/phone-number-options
   */
  async getPhoneNumberOptions(accountId?: string | null): Promise<MessagingPhoneNumberOption[]> {
    const response = await get<{ data: MessagingPhoneNumberOption[] }>(
      '/messaging/phone-number-options',
      {
        params: isStringWithValue(accountId) ? { account_id: accountId } : undefined
      }
    )
    return response.data.data || []
  },

  /**
   * Display a listing of Message Templates for a phone number.
   * GET /api/v1/messaging/phone-numbers/{phoneNumberId}/templates
   */
  async listTemplates(phoneNumberId: string, params?: Record<string, unknown>) {
    const response = await get<{ data: MessageTemplate[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/templates`,
      { params }
    )
    return response.data
  },

  /**
   * Store a new Message Template.
   * POST /api/v1/messaging/phone-numbers/{phoneNumberId}/templates
   */
  async createTemplate(
    phoneNumberId: string,
    data: { label: string; description?: string | null; text: string }
  ) {
    const response = await post<{ data: MessageTemplate[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/templates`,
      data
    )
    return response.data
  },

  /**
   * Update the specified Message Template.
   * PUT /api/v1/messaging/templates/{id}
   */
  async updateTemplate(
    id: string,
    data: { label: string; description?: string | null; text: string }
  ) {
    const response = await put<{ data: MessageTemplate[] }>(`/messaging/templates/${id}`, data)
    return response.data
  },

  /**
   * Delete the specified Message Template (soft-delete).
   * DELETE /api/v1/messaging/templates/{id}
   */
  async deleteTemplate(id: string) {
    const response = await del(`/messaging/templates/${id}`)
    return response.data
  },

  /**
   * Restore a soft-deleted Message Template.
   * POST /api/v1/messaging/templates/{id}/restore
   */
  async restoreTemplate(id: string) {
    const response = await post<{ data: MessageTemplate[] }>(`/messaging/templates/${id}/restore`)
    return response.data
  },

  /**
   * Display a listing of SMS Opt-Outs for a phone number.
   * GET /api/v1/messaging/phone-numbers/{phoneNumberId}/sms-opt-outs
   */
  async listSmsOptOuts(phoneNumberId: string, params?: Record<string, unknown>) {
    const response = await get<{ data: SmsOptOut[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/sms-opt-outs`,
      { params }
    )
    return response.data
  },

  /**
   * Store a new SMS Opt-Out for a phone number.
   * POST /api/v1/messaging/phone-numbers/{phoneNumberId}/sms-opt-outs
   */
  async createSmsOptOut(phoneNumberId: string, data: { phone_number: string; iso?: string }) {
    const response = await post<{ data: SmsOptOut[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/sms-opt-outs`,
      data
    )
    return response.data
  },

  /**
   * Delete an SMS Opt-Out record.
   * DELETE /api/v1/messaging/sms-opt-outs/{id}
   */
  async deleteSmsOptOut(id: string) {
    const response = await del(`/messaging/sms-opt-outs/${id}`)
    return response.data
  },

  /**
   * Display messaging settings for a phone number.
   * GET /api/v1/messaging/phone-numbers/{phoneNumberId}/settings
   */
  async getSettings(phoneNumberId: string): Promise<MessagingSettings> {
    const response = await get<{ data: MessagingSettings[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/settings`
    )
    const first = response.data.data?.[0]
    return (
      first || {
        sms_auto_response_enabled: false,
        sms_auto_response_text: null,
        sms_auto_response_limit_days: null,
        sms_to_email: null
      }
    )
  },

  /**
   * Update messaging settings for a phone number.
   * PATCH /api/v1/messaging/phone-numbers/{phoneNumberId}/settings
   */
  async updateSettings(phoneNumberId: string, data: MessagingSettings): Promise<MessagingSettings> {
    const response = await patch<{ data: MessagingSettings[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/settings`,
      data
    )
    const first = response.data.data?.[0]
    return first || data
  },

  /** GET /messaging/phone-numbers/{id}/threads */
  async listThreads(
    phoneNumberId: string,
    params?: Record<string, unknown>
  ): Promise<Paginated<MessageThread>> {
    const response = await get<Paginated<MessageThread>>(
      `/messaging/phone-numbers/${phoneNumberId}/threads`,
      { params }
    )
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    }
  },

  /** GET /messaging/unread-threads (newest first, across all accessible DIDs) */
  async listUnreadThreads(limit?: number): Promise<MessageThread[]> {
    const response = await get<{ data: MessageThread[] }>('/messaging/unread-threads', {
      params: limit ? { limit } : undefined
    })
    return response.data.data || []
  },

  /** GET /messaging/threads/{id} */
  async getThread(threadId: string): Promise<MessageThread | null> {
    const response = await get<{ data: MessageThread[] }>(`/messaging/threads/${threadId}`)
    return response.data.data?.[0] ?? null
  },

  /** GET /messaging/threads/{id}/messages (includes `scheduled` collection) */
  async listMessages(
    threadId: string,
    params?: Record<string, unknown>
  ): Promise<Paginated<Message> & { scheduled: Message[] }> {
    const response = await get<Paginated<Message> & { scheduled?: Message[] }>(
      `/messaging/threads/${threadId}/messages`,
      { params }
    )
    return {
      data: response.data.data || [],
      scheduled: response.data.scheduled || [],
      pagination: response.data.pagination
    }
  },

  /** PUT /messaging/messages/{id}/schedule */
  async rescheduleMessage(
    messageId: string,
    scheduledAt: string,
    timezone?: string | null
  ): Promise<Message | null> {
    const response = await put<{ data: Message[] }>(`/messaging/messages/${messageId}/schedule`, {
      scheduled_at: scheduledAt,
      timezone: timezone || undefined
    })
    return response.data.data?.[0] ?? null
  },

  /** DELETE /messaging/messages/{id}/schedule */
  async deleteScheduledMessage(messageId: string): Promise<void> {
    await del(`/messaging/messages/${messageId}/schedule`)
  },

  /** POST /messaging/messages/{id}/send-now */
  async sendScheduledMessageNow(messageId: string): Promise<Message | null> {
    const response = await post<{ data: Message[] }>(`/messaging/messages/${messageId}/send-now`)
    return response.data.data?.[0] ?? null
  },

  /** POST /messaging/phone-numbers/{id}/messages (multipart) */
  async sendNewMessage(phoneNumberId: string, formData: FormData): Promise<MessageThread | null> {
    const response = await post<{ data: MessageThread[] }>(
      `/messaging/phone-numbers/${phoneNumberId}/messages`,
      formData,
      { timeout: 120000 }
    )
    return response.data.data?.[0] ?? null
  },

  /** POST /messaging/threads/{id}/reply (multipart) */
  async replyMessage(threadId: string, formData: FormData): Promise<MessageThread | null> {
    const response = await post<{ data: MessageThread[] }>(
      `/messaging/threads/${threadId}/reply`,
      formData,
      { timeout: 120000 }
    )
    return response.data.data?.[0] ?? null
  },

  /** PATCH /messaging/threads/{id}/read */
  async markThreadRead(threadId: string, isRead: boolean): Promise<MessageThread | null> {
    const response = await patch<{ data: MessageThread[] }>(`/messaging/threads/${threadId}/read`, {
      is_read: isRead
    })
    return response.data.data?.[0] ?? null
  },

  /** POST /messaging/threads/bulk-read */
  async bulkMarkThreadsRead(threadIds: string[], isRead: boolean): Promise<MessageThread[]> {
    const response = await post<{ data: MessageThread[] }>('/messaging/threads/bulk-read', {
      thread_ids: threadIds,
      is_read: isRead
    })
    return response.data.data ?? []
  },

  /** PATCH /messaging/threads/{id}/pin */
  async toggleThreadPin(threadId: string): Promise<MessageThread | null> {
    const response = await patch<{ data: MessageThread[] }>(`/messaging/threads/${threadId}/pin`)
    return response.data.data?.[0] ?? null
  },

  /** DELETE /messaging/threads/{id} */
  async deleteThread(threadId: string): Promise<void> {
    await del(`/messaging/threads/${threadId}`)
  },

  /** POST /messaging/threads/bulk-delete */
  async bulkDeleteThreads(threadIds: string[]): Promise<void> {
    await post('/messaging/threads/bulk-delete', { thread_ids: threadIds })
  },

  /** POST /messaging/threads/{id}/restore */
  async restoreThread(threadId: string): Promise<MessageThread | null> {
    const response = await post<{ data: MessageThread[] }>(`/messaging/threads/${threadId}/restore`)
    return response.data.data?.[0] ?? null
  },

  /** POST /messaging/threads/bulk-restore */
  async bulkRestoreThreads(threadIds: string[]): Promise<MessageThread[]> {
    const response = await post<{ data: MessageThread[] }>('/messaging/threads/bulk-restore', {
      thread_ids: threadIds
    })
    return response.data.data ?? []
  },

  /** POST /messaging/segment-calculator */
  async calculateSegments(message: string): Promise<SmsSegmentsResult> {
    const response = await post<SmsSegmentsResult>('/messaging/segment-calculator', { message })
    return response.data
  },

  /** GET /messaging/bulk/example-csv */
  async downloadBulkExampleCsv(): Promise<void> {
    const response = await get<Blob>('/messaging/bulk/example-csv', { responseType: 'blob' })
    const blob = new Blob([response.data], {
      type: (response.headers['content-type'] as string) || 'text/csv'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'import-example.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },

  /** POST /messaging/phone-numbers/{id}/bulk/preview-csv */
  async previewBulkCsv(phoneNumberId: string, file: File): Promise<BulkCsvPreview> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await post<{ data: BulkCsvPreview }>(
      `/messaging/phone-numbers/${phoneNumberId}/bulk/preview-csv`,
      formData,
      { timeout: 120000 }
    )
    return response.data.data
  },

  /** POST /messaging/phone-numbers/{id}/bulk/send */
  async sendBulk(phoneNumberId: string, formData: FormData): Promise<BulkSendResult> {
    const response = await post<{ data: BulkSendResult }>(
      `/messaging/phone-numbers/${phoneNumberId}/bulk/send`,
      formData,
      { timeout: 120000 }
    )
    return response.data.data
  }
}

function isStringWithValue(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0
}
